import { useState, useCallback, useRef } from 'react';
import * as Cesium from 'cesium';
import { exportJsonFile, importJsonFile } from '../utils/fileExportUtils';

/**
 * Custom hook for managing annotations (pin markers + text labels) in Cesium viewer
 */
export const useAnnotations = (viewer) => {
  const [annotations, setAnnotations] = useState([]);
  const [highlightedAnnotation, setHighlightedAnnotation] = useState(null);
  const [annotationMode, setAnnotationMode] = useState(false);
  const entitiesRef = useRef(new Map()); // Store Cesium entities by annotation ID

  const generateId = useCallback(() => {
    return `annotation_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
  }, []);

  /**
   * Gets the current camera state from the Cesium viewer
   */
  const getCurrentCameraState = useCallback(() => {
    if (!viewer || !viewer.camera) return null;

    const camera = viewer.camera;
    return {
      position: {
        x: camera.position.x,
        y: camera.position.y,
        z: camera.position.z
      },
      heading: camera.heading,
      pitch: camera.pitch,
      roll: camera.roll
    };
  }, [viewer]);

  /**
   * Draws a black circle pin marker with a white outline
   */
  const createPinCanvas = useCallback(() => {
    const size = 18;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    const cx = size / 2;
    const cy = size / 2;
    const radius = size / 2 - 2;

    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
    ctx.fill();
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 1.5;
    ctx.stroke();

    return canvas;
  }, []);

  /**
   * Draws a rounded-rect label canvas with text
   */
  const createLabelCanvas = useCallback((text) => {
    const fontSize = 13;
    const font = `${fontSize}px sans-serif`;
    const padX = 10;
    const padY = 6;
    const radius = 6;

    // Measure text
    const measure = document.createElement('canvas').getContext('2d');
    measure.font = font;
    const metrics = measure.measureText(text);
    const w = Math.ceil(metrics.width) + padX * 2;
    const h = fontSize + padY * 2;

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');

    // Rounded rect background
    ctx.beginPath();
    ctx.moveTo(radius, 0);
    ctx.lineTo(w - radius, 0);
    ctx.quadraticCurveTo(w, 0, w, radius);
    ctx.lineTo(w, h - radius);
    ctx.quadraticCurveTo(w, h, w - radius, h);
    ctx.lineTo(radius, h);
    ctx.quadraticCurveTo(0, h, 0, h - radius);
    ctx.lineTo(0, radius);
    ctx.quadraticCurveTo(0, 0, radius, 0);
    ctx.closePath();
    ctx.fillStyle = 'rgba(0, 0, 0, 0.65)';
    ctx.fill();

    // Text
    ctx.font = font;
    ctx.fillStyle = '#fff';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, padX, h / 2);

    return canvas;
  }, []);

  /**
   * Creates pin + label billboard entities for an annotation
   */
  const createAnnotationEntities = useCallback((annotation) => {
    if (!viewer || !viewer.entities) return null;

    const position = new Cesium.Cartesian3(
      annotation.position.x,
      annotation.position.y,
      annotation.position.z
    );

    const pinCanvas = createPinCanvas();
    const labelCanvas = createLabelCanvas(annotation.text);

    const label = viewer.entities.add({
      id: `annotation_label_${annotation.id}`,
      position,
      billboard: {
        image: labelCanvas.toDataURL(),
        verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
        horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
        pixelOffset: new Cesium.Cartesian2(0, -12),
        heightReference: Cesium.HeightReference.NONE,
        scale: 1.0,
        disableDepthTestDistance: Number.POSITIVE_INFINITY
      }
    });

    const pin = viewer.entities.add({
      id: `annotation_pin_${annotation.id}`,
      position,
      billboard: {
        image: pinCanvas.toDataURL(),
        verticalOrigin: Cesium.VerticalOrigin.CENTER,
        heightReference: Cesium.HeightReference.NONE,
        scale: 1.0,
        disableDepthTestDistance: Number.POSITIVE_INFINITY
      }
    });

    return { pin, label };
  }, [viewer, createPinCanvas, createLabelCanvas]);

  /**
   * Add a new annotation at the given position
   */
  const addAnnotation = useCallback((position, text, cameraState) => {
    const annotation = {
      id: generateId(),
      text,
      timestamp: Date.now(),
      position: { x: position.x, y: position.y, z: position.z },
      cameraState: cameraState || getCurrentCameraState(),
      color: '#FF4444'
    };

    setAnnotations(prev => [...prev, annotation]);

    const entity = createAnnotationEntities(annotation);
    if (entity) {
      entitiesRef.current.set(annotation.id, entity);
    }

    return annotation;
  }, [generateId, getCurrentCameraState, createAnnotationEntities]);

  /**
   * Delete an annotation and its Cesium entities
   */
  const deleteAnnotation = useCallback((annotationId) => {
    const entities = entitiesRef.current.get(annotationId);
    if (entities && viewer && viewer.entities) {
      if (entities.pin) viewer.entities.remove(entities.pin);
      if (entities.label) viewer.entities.remove(entities.label);
      entitiesRef.current.delete(annotationId);
    }
    setAnnotations(prev => prev.filter(a => a.id !== annotationId));
    if (highlightedAnnotation === annotationId) {
      setHighlightedAnnotation(null);
    }
  }, [viewer, highlightedAnnotation]);

  /**
   * Clear all annotations
   */
  const clearAllAnnotations = useCallback(() => {
    if (viewer && viewer.entities) {
      entitiesRef.current.forEach((entities) => {
        if (entities.pin) viewer.entities.remove(entities.pin);
        if (entities.label) viewer.entities.remove(entities.label);
      });
    }
    entitiesRef.current.clear();
    setAnnotations([]);
    setHighlightedAnnotation(null);
  }, [viewer]);

  /**
   * Fly camera to an annotation entity, framing it in view
   */
  const flyToAnnotation = useCallback((annotation) => {
    if (!viewer || !annotation) return;

    const entities = entitiesRef.current.get(annotation.id);
    if (entities?.pin) {
      viewer.flyTo(entities.pin, {
        duration: 1.2,
        offset: new Cesium.HeadingPitchRange(
          annotation.cameraState?.heading ?? 0,
          -0.3,
          15
        )
      });
    }
  }, [viewer]);

  /**
   * Highlight an annotation by scaling its pin
   */
  const highlightAnnotation = useCallback((annotationId) => {
    // Reset all pin scales
    entitiesRef.current.forEach((entities, id) => {
      if (entities?.pin?.billboard) {
        entities.pin.billboard.scale = id === annotationId ? 1.4 : 1.0;
      }
    });
    setHighlightedAnnotation(annotationId);
  }, []);

  /**
   * Clear annotation highlight
   */
  const clearHighlight = useCallback(() => {
    highlightAnnotation(null);
  }, [highlightAnnotation]);

  /**
   * Show all annotation entities
   */
  const showAnnotations = useCallback(() => {
    if (!viewer || !viewer.entities) return;

    annotations.forEach((annotation) => {
      if (!entitiesRef.current.has(annotation.id)) {
        const entity = createAnnotationEntities(annotation);
        if (entity) {
          entitiesRef.current.set(annotation.id, entity);
        }
      }
    });
  }, [viewer, annotations, createAnnotationEntities]);

  /**
   * Hide all annotation entities
   */
  const hideAnnotations = useCallback(() => {
    if (!viewer || !viewer.entities) return;

    entitiesRef.current.forEach((entities) => {
      if (entities.pin) viewer.entities.remove(entities.pin);
      if (entities.label) viewer.entities.remove(entities.label);
    });
    entitiesRef.current.clear();
    setHighlightedAnnotation(null);
  }, [viewer]);

  /**
   * Export annotations as JSON
   */
  const exportAnnotations = useCallback(() => {
    const data = {
      annotations,
      exportedAt: new Date().toISOString(),
      version: '1.0'
    };
    exportJsonFile(`cesium_annotations_${new Date().toISOString().split('T')[0]}.json`, data);
  }, [annotations]);

  /**
   * Import annotations from JSON file
   */
  const importAnnotations = useCallback((file) => {
    return importJsonFile(file).then((data) => {
      if (data.annotations && Array.isArray(data.annotations)) {
        data.annotations.forEach((annotation) => {
          const entity = createAnnotationEntities(annotation);
          if (entity) {
            entitiesRef.current.set(annotation.id, entity);
          }
        });
        setAnnotations(prev => [...prev, ...data.annotations]);
        return data.annotations.length;
      }
      throw new Error('Invalid annotations file format');
    });
  }, [createAnnotationEntities]);

  return {
    annotations,
    highlightedAnnotation,
    annotationMode,
    setAnnotationMode,
    addAnnotation,
    deleteAnnotation,
    clearAllAnnotations,
    flyToAnnotation,
    highlightAnnotation,
    clearHighlight,
    showAnnotations,
    hideAnnotations,
    exportAnnotations,
    importAnnotations,
    getCurrentCameraState
  };
};
